import type { FC, FormEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { Loader2, MailCheck } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import AuthShell from '@/components/account/auth-shell'
import { TextField } from '@/components/generals/form-field'
import { apiRequest, errorMessage } from '@/lib/client/api'

const ForgotPasswordPage: FC = (): ReactNode => {
    const router = useRouter()
    const staff = router.query.staff === '1'

    const [email, setEmail] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [sent, setSent] = useState(false)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setBusy(true)
        setError(null)

        try {
            await apiRequest('/api/auth/forgot', { method: 'POST', body: { email } })
            setSent(true)
        }
        catch (problem) {
            setError(errorMessage(problem))
        }
        finally {
            setBusy(false)
        }
    }

    return (
        <AuthShell
            eyebrow={staff ? 'Backoffice' : 'Mijn ArtDoors'}
            title='Wachtwoord vergeten'
            description='Vul uw e-mailadres in. U ontvangt een link waarmee u een nieuw wachtwoord kiest.'
        >
            <Seo title='Wachtwoord vergeten' noIndex />

            {sent ? (
                <div className='flex flex-col gap-4 rounded-md border border-border bg-card p-6'>
                    <MailCheck className='size-7 text-brass-strong' aria-hidden='true' />
                    <p className='text-sm leading-relaxed'>
                        Als er een account bestaat voor <strong>{email}</strong>, ontvangt u binnen enkele minuten een e-mail met een herstellink. Kijk ook even in uw spammap.
                    </p>
                    <Link href={staff ? '/backoffice/inloggen' : '/account/inloggen'} className='text-sm font-medium underline underline-offset-4'>
                        Terug naar inloggen
                    </Link>
                </div>
            ) : (
                <form onSubmit={submit} className='flex flex-col gap-4' noValidate>
                    <TextField label='E-mailadres' type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />

                    {error && <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive' role='alert'>{error}</p>}

                    <button type='submit' disabled={busy} className='inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-60'>
                        {busy && <Loader2 className='size-4 animate-spin' aria-hidden='true' />}
                        Herstellink versturen
                    </button>

                    <Link href={staff ? '/backoffice/inloggen' : '/account/inloggen'} className='text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'>
                        Terug naar inloggen
                    </Link>
                </form>
            )}
        </AuthShell>
    )
}

export default ForgotPasswordPage
